import type { FunctionalComponent } from 'preact';
import type { LegalBlock, LegalDocument, LegalTable } from '@/data/legal.ts';

/** 본문의 `\n` 을 줄바꿈으로 그린다 */
const Lines: FunctionalComponent<{ text: string }> = ({ text }) => (
  <>
    {text.split('\n').map((line, i) => (
      <>
        {i > 0 && <br />}
        {line}
      </>
    ))}
  </>
);

/**
 * 시안의 표. 한 `<table>` 에 `<thead>`+`<tbody>` 쌍이 여러 번 반복될 수 있다
 * (개인정보 처리방침 제2조는 4묶음).
 */
const Table: FunctionalComponent<{ table: LegalTable }> = ({ table }) => (
  <table>
    <colgroup>
      {table.columns.map((width, i) => (
        <col key={i} style={{ width }} />
      ))}
    </colgroup>
    {table.groups.map((group, g) => (
      <>
        {group.heading && (
          <thead key={`h${g}`}>
            <tr>
              <th colSpan={table.columns.length}>{group.heading}</th>
            </tr>
          </thead>
        )}
        <tbody key={`b${g}`}>
          {group.rows.map((row, r) => (
            <tr key={r}>
              {row.map((cell, c) => (
                <td key={c} class={cell.type ? 'type' : undefined} colSpan={cell.colSpan}>
                  <Lines text={cell.text} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </>
    ))}
  </table>
);

const Block: FunctionalComponent<{ block: LegalBlock }> = ({ block }) => {
  if (block.kind === 'table') return <Table table={block.table} />;
  if (block.kind === 'cont')
    return (
      // 안에 글·표·cont 가 섞여 들어온다. 시안처럼 div.cont 를 겹쳐 들여쓴다.
      <div class="cont">
        {block.blocks.map((child, i) => (
          <Block key={i} block={child} />
        ))}
      </div>
    );
  // 연달아 오는 글 블록이 한 줄로 붙지 않게 블록마다 줄을 나눈다 (시안의 dd·p 한 덩어리).
  return (
    <div class="legal-text">
      <Lines text={block.text} />
    </div>
  );
};

/**
 * 약관·방침 본문 (시안 `.popup-detail` 안쪽).
 *
 * 마크업을 시안과 똑같이 `<dl><dt><dd>` 로 유지한다. 스타일이 그 구조에 걸려 있다.
 */
export const LegalDocumentBody: FunctionalComponent<{ doc: LegalDocument }> = ({ doc }) => (
  <>
    <h1>{doc.title}</h1>
    {doc.sections.map((section, s) => (
      <dl key={s}>
        {section.title && <dt>{section.title}</dt>}
        <dd>
          {section.blocks.map((block, b) => (
            <Block key={b} block={block} />
          ))}
        </dd>
      </dl>
    ))}
  </>
);
